// src/components/EventSteps/EventInfoStep.tsx
import React, { useCallback, useEffect, useState } from 'react';
import {
    Grid,
    Card,
    FormControl,
    FormLabel,
    Input,
    Box,
    Typography,
    Button, Stack,
} from '@mui/joy';
import { AddPhotoAlternate, ChevronRight } from '@mui/icons-material';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { useDropzone } from 'react-dropzone';
import { useEventForm } from '../../context/EventFormContext';
const getCurrentDateTimeLocal = (): string => {
    const now = new Date();
    const offset = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - offset).toISOString().slice(0, 16);
};

const formatDatetimeLocal = (dateString: string): string => {
    console.log(dateString);
    const date = new Date(dateString);
    const pad = (num: number): string => String(num).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const EventInfoStep: React.FC = () => {
    const {
        firstFormData,
        updateFirstFormData,
        editorState,
        setEditorState,
        fileInputRef,
        uploadBanner,
        validateStep1,
        setStep,
    } = useEventForm();

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            if (acceptedFiles.length > 0) {
                uploadBanner(acceptedFiles[0]);
                updateFirstFormData('icon', acceptedFiles[0]);
            }
        },
        [uploadBanner, updateFirstFormData]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'image/*': [] },
    });

    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    useEffect(() => {
        if (firstFormData.icon) {
            if (firstFormData.icon.url) {
                setPreviewUrl(firstFormData.icon.url);
            } else if (firstFormData.icon instanceof File) {
                const objectUrl = URL.createObjectURL(firstFormData.icon);
                setPreviewUrl(objectUrl);
                return () => URL.revokeObjectURL(objectUrl);
            }
        } else {
            setPreviewUrl(null);
        }
    }, [firstFormData.icon]);

    useEffect(() => {
        if (!firstFormData.price) {
            updateFirstFormData('price', 0);
        }
    }, [firstFormData.price]);



    return (
        <>
            {/* Navigation */}
            <Stack direction="row" justifyContent="end" spacing={2}>
                <Button
                    size="lg"
                    variant="solid"
                    onClick={async () => {
                        if (await validateStep1()) {
                            setStep((prev) => prev + 1)
                        }
                    }}
                    endDecorator={<ChevronRight />}
                >
                    Suivant
                </Button>
            </Stack>

            <Card variant="outlined" sx={{ p: 3, mt: 2 }}>
                <Grid container spacing={2}>
                    <Grid xs={8}>
                        <FormControl>
                            <FormLabel>
                                Nom de l&apos;évènement <span className="text-red-500">*</span>
                            </FormLabel>
                            <Input
                                variant="outlined"
                                value={firstFormData.name || ''}
                                onChange={(e) =>
                                    updateFirstFormData('name', e.target.value)
                                }
                            />
                        </FormControl>
                    </Grid>
                    <Grid xs={12}>
                        <FormControl>
                            <FormLabel>
                                Description de l&apos;évènement <span className="text-red-500">*</span>
                            </FormLabel>
                            <Editor
                                editorState={editorState}
                                toolbarClassName="toolbarClassName"
                                wrapperClassName="wrapperClassName"
                                editorClassName="editorClassName"
                                onEditorStateChange={setEditorState}
                                wrapperStyle={{
                                    background: '#FFFDFA',
                                    borderRadius: '5px',
                                    border: '1px solid #cdd7e1',
                                }}
                                editorStyle={{
                                    height: '200px',
                                    padding: '10px',
                                }}
                            />
                        </FormControl>
                    </Grid>
                </Grid>
            </Card>

            <Card variant="outlined" sx={{ p: 3, mt: 3 }}>
                <Grid container spacing={2}>
                    <Grid xs={12}>
                        <FormControl>
                            <FormLabel>
                                Affiche de l&apos;évènement <span className="text-red-500">*</span>
                            </FormLabel>
                            <Box
                                {...getRootProps()}
                                sx={{
                                    border: '2px dashed',
                                    borderColor: 'neutral.outlinedBorder',
                                    borderRadius: 'md',
                                    padding: 2,
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    '&:hover': { borderColor: 'primary.outlinedBorder' },
                                }}
                            >
                                <input accept={"image/png, image/jpeg, image/webp"} {...getInputProps()} />
                                {isDragActive ? (
                                    <Typography>Déposez l&apos;image ici...</Typography>
                                ) : (
                                    <>
                                        <AddPhotoAlternate fontSize="large" color="disabled" />
                                        <Typography sx={{ mt: 1 }}>
                                            Glissez-déposez une image ici ou cliquez pour sélectionner un fichier
                                        </Typography>
                                        {firstFormData.icon?.name && (
                                            <Typography className="mt-2">
                                                Fichier sélectionné : {firstFormData.icon?.name}
                                            </Typography>
                                        )}
                                    </>
                                )}
                            </Box>
                            {previewUrl && (
                                <Box sx={{ mt: 2, textAlign: 'center' }}>
                                    <img
                                        src={previewUrl}
                                        alt="Aperçu"
                                        style={{ maxHeight: 150, maxWidth: '100%', borderRadius: 8 }}
                                    />
                                </Box>
                            )}
                        </FormControl>
                    </Grid>
                </Grid>
            </Card>

            <Card variant="outlined" sx={{ p: 3, mt: 3 }}>
                <Grid container spacing={2}>
                    <Grid xs={12}>
                        <FormLabel>
                            Début <span className="text-red-500">*</span>
                        </FormLabel>
                    </Grid>
                    <Grid xs={6}>
                        <Input
                            variant="outlined"
                            type="datetime-local"
                            slotProps={{
                                input: { min: getCurrentDateTimeLocal() },
                            }}
                            onChange={(e) => {
                                updateFirstFormData('start_date', e.target.value);
                                const [date, time] = e.target.value.split('T');
                                updateFirstFormData('start_hour', time);
                            }}
                            value={
                                firstFormData.start_date
                                    ? formatDatetimeLocal(firstFormData.start_date)
                                    : ''
                            }
                        />
                    </Grid>
                    <Grid xs={12} sx={{ mt: 2 }}>
                        <FormLabel>
                            Fin <span className="text-red-500">*</span>
                        </FormLabel>
                    </Grid>
                    <Grid xs={6}>
                        <Input
                            variant="outlined"
                            type="datetime-local"
                            slotProps={{
                                input: {
                                    min: firstFormData.start_date
                                        ? formatDatetimeLocal(firstFormData.start_date)
                                        : getCurrentDateTimeLocal(),
                                },
                            }}
                            onChange={(e) => {
                                updateFirstFormData('end_date', e.target.value);
                                const [date, time] = e.target.value.split('T');
                                updateFirstFormData('end_hour', time);
                            }}
                            value={
                                firstFormData.end_date
                                    ? formatDatetimeLocal(firstFormData.end_date)
                                    : ''
                            }
                        />
                    </Grid>
                </Grid>
            </Card>

            <Card variant="outlined" sx={{ p: 3, mt: 3 }}>
                <Grid container spacing={2}>
                    <Grid xs={3}>
                        <FormControl>
                            <FormLabel>
                                Prix de l&apos;évènement <span className="text-red-500">*</span>
                            </FormLabel>
                            <Input
                                variant="outlined"
                                type="number"
                                defaultValue={0.0}
                                endDecorator={<>&euro;</>}
                                slotProps={{
                                    input: { step: '0.01', min: '0' },
                                }}
                                value={firstFormData.price}
                                onChange={(e) =>
                                    updateFirstFormData('price', parseFloat(e.target.value))
                                }
                            />
                        </FormControl>
                    </Grid>
                    <Grid xs={6}>
                        <FormControl>
                            <FormLabel>
                                Lien vers la billetterie
                            </FormLabel>
                            <Input
                                variant="outlined"
                                value={firstFormData.ticketing_link || ''}
                                onChange={(e) =>
                                    updateFirstFormData('ticketing_link', e.target.value)
                                }
                            />
                            <small>
                                <i>Url de la billetterie / Mail ou tél de réservation</i>
                            </small>
                        </FormControl>
                    </Grid>
                </Grid>
            </Card>
        </>
    );
};

export default EventInfoStep;